import { Button, cn, Icon, Menu, Select, Skeleton, TextField } from '@starter/ui';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { FieldRow } from '../brands/field-row.tsx';
import { useCatalog } from '../catalogs/use-catalog.ts';
import { authClient } from '../lib/auth.ts';
import type { FieldValue } from '../lib/field-value.ts';
import { provenanceKey } from '../lib/provenance-key.ts';
import { useSectionInView } from '../lib/section-in-view.ts';
import { useFeatureFlag } from '../observability/use-feature-flag.ts';
import {
  useAddPhoto,
  useArchiveProduct,
  useProduct,
  useRemovePhoto,
  useRestoreProduct,
  useSetAudience,
  useSetPhotoRole,
  useUpdateProduct,
} from '../products/api.ts';
import { PhotoGrid } from '../products/photo-grid.tsx';
import { Photograph } from '../products/photograph.tsx';
import { Form, Section } from '../products/section.tsx';

/**
 * A product, the whole of it — built on the measurements in
 * `prd/measured/products.json`, taken off the live product on 2026-09-21.
 *
 * Three things this page does that the brand page does not, all measured:
 *
 *  - **its sections are not boxes.** Heading and fields sit on the page
 *    colour; `Section` is here rather than `Panel` for that one reason.
 *  - **its forms stop at 640** inside a column of 1392. The photographs and
 *    the generations are what use the full width.
 *  - **its breadcrumb is three deep** — the list, the brand, the product — so
 *    the facts line under the name is free to be about the product alone.
 *
 * The enrichment sections are behind `product-enrichment-fields`. With the
 * flag off this is description, price, photographs: a coherent screen, not a
 * broken one (`apps/api/src/flags.ts`).
 */
type SectionId =
  | 'about'
  | 'offer'
  | 'what-to-say'
  | 'visual'
  | 'audience'
  | 'photos'
  | 'generations';

const GROUPS: { title: string; items: { id: SectionId; label: string; enriched?: boolean }[] }[] = [
  {
    title: 'Product',
    items: [
      { id: 'about', label: 'About' },
      { id: 'offer', label: 'Offer', enriched: true },
      { id: 'what-to-say', label: 'What to say', enriched: true },
      { id: 'visual', label: 'Visual guidelines', enriched: true },
      { id: 'audience', label: 'Target audience', enriched: true },
    ],
  },
  {
    title: 'Content',
    items: [
      { id: 'photos', label: 'Photographs' },
      { id: 'generations', label: 'Generations' },
    ],
  },
];

/**
 * The same ids, in the order they appear down the page.
 *
 * Derived rather than written again: the nav's groups already say both which
 * sections there are and what order they come in, and a second list would be
 * the one that goes stale the day a section is added.
 */
const SECTION_IDS = GROUPS.flatMap((group) => group.items.map((item) => item.id));

function ProductDetail() {
  const { productId } = Route.useParams();
  const product = useProduct(productId);
  const update = useUpdateProduct(productId);
  const setAudience = useSetAudience(productId);
  const addPhoto = useAddPhoto(productId);
  const setRole = useSetPhotoRole(productId);
  const removePhoto = useRemovePhoto(productId);
  const archive = useArchiveProduct(productId);
  const restore = useRestoreProduct(productId);
  const categories = useCatalog('product_categories');
  const currencies = useCatalog('product_currencies');
  const enriched = useFeatureFlag('product-enrichment-fields');
  const [problem, setProblem] = useState<string | undefined>();
  const [section, setSection] = useState<SectionId>('about');

  /**
   * The nav scrolls; it does not swap the page for one section of it.
   *
   * And since 2026-09-25 it also *follows*: scrolling by hand moves the
   * highlight, which it did not — the owner found a nav that lit up only when
   * pressed, on a page whose whole shape is one long scroll.
   *
   * Up here with the other hooks rather than beside the `go` it belongs to:
   * the page returns early while it is loading and again when the product is
   * missing, so anything called below those is called only sometimes.
   */
  const startedByClick = useSectionInView(SECTION_IDS, section, setSection, !product.isPending);
  const [more, setMore] = useState(false);

  if (product.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (product.error || !product.data) {
    return (
      <div className="flex flex-col items-start gap-4">
        <h1 className="font-display text-2xl font-bold text-ink">Product not found</h1>
        <Button render={<Link search={{ status: 'active' }} to="/products" />} variant="secondary">
          Back to my products
        </Button>
      </div>
    );
  }

  const { product: record, photos, provenance, audience, brand } = product.data;
  const archived = record.status === 'archived';

  const save = (field: string) => (value: FieldValue) => {
    setProblem(undefined);
    update.mutate({ [field]: value }, { onError: (error) => setProblem(error.message) });
  };

  const go = (next: SectionId) => {
    startedByClick(next);
    document.getElementById(next)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const tags = record.categoryTags ?? [];
  const toggleTag = (key: string) => {
    const next = tags.includes(key)
      ? tags.filter((one) => one !== key)
      : tags.length >= 4
        ? tags
        : [...tags, key];
    if (next !== tags) save('categoryTags')(next as unknown as FieldValue);
  };

  return (
    <div className="flex flex-col gap-8">
      <Crumbs brandId={record.brandId} brandName={brand?.name} name={record.name} />

      <div className="flex gap-10">
        <nav
          className="sticky top-8 hidden h-fit w-50 flex-none flex-col gap-0.5 lg:flex"
          data-fidelity="product-nav"
        >
          {GROUPS.map((group) => (
            <div className="flex flex-col gap-0.5" key={group.title}>
              <span
                className="px-5 text-2xs leading-9 tracking-[0.6px] text-ink-muted uppercase"
                data-fidelity="product-nav-group"
              >
                {group.title}
              </span>
              {group.items
                .filter((item) => enriched || !item.enriched)
                .map((item) => (
                  <button
                    // Said to the browser as well as drawn: which section is
                    // current is a fact a screen reader needs and a fill alone
                    // does not carry.
                    aria-current={item.id === section ? 'true' : undefined}
                    className={cn(
                      'flex h-12 cursor-pointer items-center rounded-full px-5 text-left text-[16px] leading-[22.4px]',
                      item.id === section
                        ? 'bg-brand-200 font-semibold text-ink'
                        : 'font-normal text-ink hover:bg-surface-muted',
                    )}
                    data-testid={`product-nav-${item.id}`}
                    key={item.id}
                    onClick={() => go(item.id)}
                    type="button"
                  >
                    {item.label}
                  </button>
                ))}
            </div>
          ))}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col gap-8">
          {archived ? (
            <div
              className="flex h-16 items-center gap-3 rounded-xl bg-surface-muted px-4 py-3"
              data-testid="product-archived-notice"
            >
              <span className="flex-1 text-[16px] leading-[22.4px] text-ink">
                {record.archivedWithBrand
                  ? 'Archived with its brand. Restoring the brand brings it back.'
                  : 'This product is archived.'}
              </span>
              <Button
                data-testid="product-restore"
                disabled={restore.isPending}
                onClick={() => restore.mutate()}
                variant="secondary"
              >
                Restore
              </Button>
            </div>
          ) : null}

          <Hero
            archived={archived}
            materialCount={record.materialCount}
            name={record.name}
            onArchive={() => archive.mutate()}
            photoCount={photos.length}
            uploadId={record.heroUploadId}
          />

          {/*
            Said on the product's own page rather than in a composer several
            screens later: a product with no photograph cannot be the subject
            of anything produced, and finding that out after choosing a format
            is finding it out too late.
          */}
          {record.canBeProduced ? null : (
            <div
              className="flex max-w-[640px] flex-wrap items-center gap-3 rounded-card border border-border-subtle bg-surface-raised px-4 py-3"
              data-testid="product-not-producible"
            >
              <p className="min-w-0 flex-1 text-bodysm text-ink-muted">
                There are no photographs yet, so nothing can be made from this product.
              </p>
              {/*
                The sentence above used to end there, which made it a dead end:
                it names the one thing standing between this product and an
                advert, and left the remedy several screens down the same page
                with nothing linking the two. The argument for saying it here
                at all — better now than after choosing a format — applies once
                more at this scale.
              */}
              <Button
                data-testid="product-add-first-photo"
                onClick={() => go('photos')}
                variant="secondary"
              >
                Add a photograph
              </Button>
            </div>
          )}

          {problem ? (
            <p
              className="max-w-[640px] rounded-card border border-danger bg-surface-raised px-4 py-3 text-sm text-danger-text"
              data-testid="product-problem"
              role="alert"
            >
              {problem}
            </p>
          ) : null}

          <Section id="about" testId="panel-product" title="About">
            <Form>
              <FieldRow
                label="Name"
                onSave={save('name')}
                pending={update.isPending}
                placeholder="Morning Blend 250 g"
                provenance={provenance[provenanceKey('product', 'name')]}
                readOnly={archived}
                testId="field-name"
                value={record.name}
              />
              <FieldRow
                kind="paragraph"
                label="Description"
                onSave={save('description')}
                pending={update.isPending}
                placeholder="What it is, as it reads on the label."
                provenance={provenance[provenanceKey('product', 'description')]}
                readOnly={archived}
                testId="field-description"
                value={record.description}
              />

              {/* Price and currency are one decision in two controls: a price
                  without a currency lies to the buyer. */}
              <div className="flex flex-wrap items-end gap-3">
                <TextField
                  className="min-w-[8rem] flex-1"
                  data-testid="field-price"
                  defaultValue={record.price ?? ''}
                  disabled={archived}
                  label="Price"
                  onBlur={(event) => {
                    const raw = event.currentTarget.value.trim();
                    update.mutate(
                      { price: raw === '' ? null : Number(raw) },
                      { onError: (error) => setProblem(error.message) },
                    );
                  }}
                  placeholder="12.50"
                />
                <div className="flex min-w-[8rem] flex-col gap-1.5">
                  <span className="text-bodysm leading-[16.8px] font-semibold text-ink">
                    Currency
                  </span>
                  <Select.Root
                    items={currencies.items.map((item) => ({ label: item.key, value: item.key }))}
                    onValueChange={(value: unknown) => save('currency')(String(value))}
                    value={record.currency ?? undefined}
                  >
                    <Select.Trigger
                      aria-label="Currency"
                      data-testid="field-currency"
                      disabled={archived}
                    >
                      <Select.Value placeholder="—" />
                      <Select.Icon />
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Positioner>
                        <Select.Popup>
                          <Select.List>
                            {currencies.items.map((item) => (
                              <Select.Item key={item.key} value={item.key}>
                                <Select.ItemText>
                                  {item.key} · {currencies.label(item.key)}
                                </Select.ItemText>
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.List>
                        </Select.Popup>
                      </Select.Positioner>
                    </Select.Portal>
                  </Select.Root>
                </div>
              </div>

              {enriched && more ? (
                <>
                  <FieldRow
                    kind="paragraph"
                    label="Specifications"
                    onSave={save('specs')}
                    pending={update.isPending}
                    placeholder="Weight, contents, dimensions — whatever is printed on the label."
                    provenance={provenance[provenanceKey('product', 'specs')]}
                    readOnly={archived}
                    testId="field-specs"
                    value={record.specs}
                  />
                  <CategoryTags
                    chosen={tags}
                    items={categories.items.map((item) => ({
                      key: item.key,
                      label: categories.label(item.key),
                    }))}
                    onToggle={toggleTag}
                    readOnly={archived}
                  />
                </>
              ) : null}

              {enriched ? (
                <button
                  className="cursor-pointer self-start text-[16px] leading-[22.4px] text-ink underline-offset-4 hover:underline"
                  data-testid="product-details-toggle"
                  onClick={() => setMore(!more)}
                  type="button"
                >
                  {more ? 'Hide details' : 'Add details'}
                </button>
              ) : null}
            </Form>
          </Section>

          {enriched ? (
            <>
              <Section id="offer" title="Offer">
                <Form>
                  <FieldRow
                    label="Call to action"
                    onSave={save('cta')}
                    pending={update.isPending}
                    placeholder="Two or three words, for a button"
                    provenance={provenance[provenanceKey('product', 'cta')]}
                    readOnly={archived}
                    testId="field-cta"
                    value={record.cta}
                  />
                  <Suggest />
                </Form>
              </Section>

              <Section id="what-to-say" title="What to say">
                <Form>
                  <FieldRow
                    kind="paragraph"
                    label="Key benefit"
                    onSave={save('benefits')}
                    pending={update.isPending}
                    placeholder="One sentence: what the buyer gets."
                    provenance={provenance[provenanceKey('product', 'benefits')]}
                    readOnly={archived}
                    testId="field-benefits"
                    value={record.benefits}
                  />
                  <FieldRow
                    kind="paragraph"
                    label="Proof points · up to 4"
                    onSave={save('proofPoints')}
                    pending={update.isPending}
                    placeholder="Facts that can be checked, one per line."
                    provenance={provenance[provenanceKey('product', 'proof_points')]}
                    readOnly={archived}
                    testId="field-proof-points"
                    value={record.proofPoints}
                  />
                  <FieldRow
                    kind="paragraph"
                    label="Permitted claims · up to 6"
                    onSave={save('claimsAllowed')}
                    pending={update.isPending}
                    placeholder="What may be claimed about this product, one per line."
                    provenance={provenance[provenanceKey('product', 'claims_allowed')]}
                    readOnly={archived}
                    testId="field-claims"
                    value={record.claimsAllowed}
                  />
                  <Suggest />
                </Form>
              </Section>

              <Section id="visual" title="Visual guidelines">
                <Form>
                  <FieldRow
                    kind="paragraph"
                    label="Where it lives when nobody is using it"
                    onSave={save('sceneSuggestions')}
                    pending={update.isPending}
                    placeholder="A kitchen shelf, a bag, a boot — the scene it is put into."
                    provenance={provenance[provenanceKey('product', 'scene_suggestions')]}
                    readOnly={archived}
                    testId="field-scene"
                    value={record.sceneSuggestions}
                  />
                  <FieldRow
                    label="Packshot subject"
                    onSave={save('packshotSubject')}
                    pending={update.isPending}
                    placeholder="Exactly what is in the frame on white."
                    provenance={provenance[provenanceKey('product', 'packshot_subject')]}
                    readOnly={archived}
                    testId="field-packshot"
                    value={record.packshotSubject}
                  />
                  <Suggest />
                </Form>
              </Section>

              <Section id="audience" testId="product-audience" title="Target audience">
                <Form>
                  {/*
                    Empty is a legal state, and the product says so rather than
                    nagging: an order that names no buyer gets the default
                    persona. The field writes one persona row underneath.
                  */}
                  <FieldRow
                    kind="paragraph"
                    label="Who is the buyer?"
                    onSave={(value) => setAudience.mutate(String(value ?? ''))}
                    pending={setAudience.isPending}
                    placeholder="Nobody has described the buyer — that is fine, and we will work without one."
                    provenance={undefined}
                    readOnly={archived}
                    testId="field-audience"
                    value={audience}
                  />
                  <Suggest />
                </Form>
              </Section>
            </>
          ) : null}

          <Section
            count={photos.length === 0 ? 'No photographs yet' : `${photos.length} photographs`}
            id="photos"
            testId="panel-photos"
            title="Photographs"
          >
            <div className="flex flex-col gap-4">
              {addPhoto.error ? (
                <p className="text-sm text-danger-text" data-testid="photo-error" role="alert">
                  {addPhoto.error.message}
                </p>
              ) : null}
              <PhotoGrid
                onAdd={(file) =>
                  addPhoto.mutate({ file, role: photos.length === 0 ? 'hero' : 'lifestyle' })
                }
                onRemove={(photoId) => removePhoto.mutate(photoId)}
                onRole={(photoId, role) => setRole.mutate({ photoId, role })}
                pending={addPhoto.isPending}
                photos={photos}
              />
            </div>
          </Section>

          <Section id="generations" title="Generations">
            <div className="flex flex-col items-center gap-5 rounded-card bg-surface-raised p-6 text-center">
              <Icon className="text-ink-muted" name="images" size={24} />
              <div className="flex flex-col items-center gap-3">
                <p className="text-[20px] leading-[24px] font-bold text-ink">
                  Nothing has been made from this product yet
                </p>
                <p className="text-bodysm leading-[16.8px] text-ink-muted">
                  The composers arrive with their own area; this is where their work will land.
                </p>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

/** Three levels — the list, the brand, the product. Measured. */
function Crumbs({
  brandId,
  brandName,
  name,
}: {
  brandId: string;
  brandName: string | undefined;
  name: string;
}) {
  return (
    <header className="flex h-14 items-center gap-2 pr-2 pl-4">
      <Link
        className="text-[16px] leading-[22.4px] text-ink-mid no-underline hover:text-ink"
        search={{ status: 'active' }}
        to="/products"
      >
        My products
      </Link>
      <Icon className="text-ink-muted" name="chevron-right" size={24} />
      <Link
        className="text-[16px] leading-[22.4px] text-ink-mid no-underline hover:text-ink"
        params={{ brandId }}
        to="/brands/$brandId"
      >
        {brandName ?? 'Its brand'}
      </Link>
      <Icon className="text-ink-muted" name="chevron-right" size={24} />
      <span
        className="min-w-0 truncate text-[16px] leading-[22.4px] font-semibold text-ink"
        data-testid="product-title"
      >
        {name}
      </span>
    </header>
  );
}

/**
 * 140px well, the name, the facts line, and what can be done with it.
 *
 * The well showed an icon and nothing else until 2026-09-25 — it was never
 * given a picture to show, so a product with six photographs looked exactly
 * like one with none, on the very page where the photographs are managed. The
 * icon is the *empty* state, not the state.
 */
function Hero({
  name,
  uploadId,
  photoCount,
  materialCount,
  archived,
  onArchive,
}: {
  name: string;
  uploadId: string | null;
  photoCount: number;
  materialCount: number;
  archived: boolean;
  onArchive: () => void;
}) {
  return (
    <div className="flex flex-wrap items-start gap-6" data-fidelity="product-hero">
      <div
        className="flex size-[140px] flex-none items-center justify-center overflow-hidden rounded-card bg-surface-muted"
        data-fidelity="product-hero-thumb"
      >
        {uploadId ? (
          <Photograph alt={name} className="size-full" uploadId={uploadId} />
        ) : (
          <Icon className="text-ink-faint" name="image" size={28} />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h1
          className="min-w-0 truncate font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
          data-fidelity="product-title"
        >
          {name}
        </h1>
        <p className="text-bodysm leading-[16.8px] text-ink-muted">
          {photoCount} {photoCount === 1 ? 'photograph' : 'photographs'} · {materialCount}{' '}
          {materialCount === 1 ? 'material' : 'materials'}
        </p>
      </div>
      {archived ? null : (
        <Menu.Root>
          <Menu.Trigger
            aria-label="Actions for this product"
            className="flex size-9 cursor-pointer items-center justify-center rounded-full text-ink-strong transition-colors hover:bg-surface-muted"
            data-testid="product-actions"
          >
            <Icon name="ellipsis" size={20} />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner align="end">
              <Menu.Popup>
                <Menu.Item data-testid="product-archive" onClick={onArchive}>
                  Archive this product
                </Menu.Item>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      )}
    </div>
  );
}

/**
 * Every enrichment field in the product carries one of these, and at a zero
 * balance every one of them opens the paywall rather than suggesting anything
 * — measured on 2026-09-21. We have no balance at all yet, so ours says what
 * is true and does not pretend to be a button that works.
 */
function Suggest() {
  return (
    <p className="text-bodysm leading-[16.8px] text-ink-muted" data-testid="suggest-unavailable">
      Suggesting with AI needs credits, which arrive with the money layer.
    </p>
  );
}

/** Thirteen chips, four at a time. The cap is shown, not enforced by greying. */
function CategoryTags({
  items,
  chosen,
  onToggle,
  readOnly,
}: {
  items: { key: string; label: string }[];
  chosen: string[];
  onToggle: (key: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid="category-tags">
      <span className="flex flex-wrap items-center gap-2">
        <span className="text-bodysm leading-[16.8px] font-semibold text-ink">Categories</span>
        <span className="text-bodysm leading-[16.8px] text-ink-muted">{chosen.length} of 4</span>
      </span>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <button
            className={cn(
              'cursor-pointer rounded-full border px-4 py-3 text-[16px] leading-[22.4px] transition-colors',
              chosen.includes(item.key)
                ? 'border-transparent bg-brand-200 text-ink'
                : 'border-border-subtle bg-surface text-ink hover:border-brand-300',
              readOnly && 'pointer-events-none opacity-60',
            )}
            data-testid={`category-tag-${item.key}`}
            key={item.key}
            onClick={() => onToggle(item.key)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/products/$productId')({
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: ProductDetail,
});
